/**
 * ImageGenerate - Image Generation Page Component
 * Modern UI with Tailwind CSS
 */

import React, { useState, useEffect } from 'react';
import {
    ImageIcon,
    Sparkles,
    Settings2,
    Dice5,
    RefreshCw,
    Download,
    Search,
    X,
    ChevronDown,
    ChevronUp,
    Loader2,
    AlertCircle,
    Wand2,
    Layers,
    SlidersHorizontal,
    Copy,
    ExternalLink,
    Plus,
    Minus
} from 'lucide-react';

interface LoRA {
    id: number;
    versionId: number;
    name: string;
    previewImage: string | null;
    triggerWords: string[];
    weight: number;
}

interface Checkpoint {
    id: number;
    versionId: number;
    name: string;
    previewImage: string | null;
}

interface CivitaiModel {
    id: number;
    name: string;
    nsfw: boolean;
    tags: string[];
    versionId: number;
    previewImage: string | null;
    triggerWords: string[];
}

interface Pagination {
    currentPage: number;
    pageSize: number;
    totalItems: number;
    totalPages: number;
}

interface ImageGenerateProps {
    isDarkMode: boolean;
    userData: {
        username?: string;
        subscription?: { tier: string };
    } | null;
}

const DEFAULT_NEGATIVE_PROMPT =
    "(fused_digits), (6digits:1.3), (4digits:1.5), 3digits, bad_hands, " +
    "extra_digits, missing_digits, extra_limbs, bad_anatomy, bad_proportions, " +
    "deformed, poorly_drawn, lowres, blurry, overexposed, underexposed, " +
    "chromatic_aberration, compression_artifacts, watermark, text, logo, signature,";

const RESOLUTION_PRESETS = [
    { label: 'Square', ratio: '1:1', width: 1024, height: 1024 },
    { label: 'Portrait', ratio: '2:3', width: 832, height: 1216 },
    { label: 'Landscape', ratio: '3:2', width: 1216, height: 832 }
];

const ImageGenerate: React.FC<ImageGenerateProps> = ({ isDarkMode, userData }) => {
    // Model selection state
    const [checkpoints, setCheckpoints] = useState<CivitaiModel[]>([]);
    const [selectedCheckpoint, setSelectedCheckpoint] = useState<Checkpoint | null>(null);
    const [checkpointDropdownOpen, setCheckpointDropdownOpen] = useState(false);

    // LoRA state
    const [loras, setLoras] = useState<CivitaiModel[]>([]);
    const [selectedLoras, setSelectedLoras] = useState<LoRA[]>([]);
    const [loraSearch, setLoraSearch] = useState('');
    const [loraPage, setLoraPage] = useState(1);
    const [loraPagination, setLoraPagination] = useState<Pagination | null>(null);
    const [loadingLoras, setLoadingLoras] = useState(false);

    // Prompt state
    const [prompt, setPrompt] = useState('');
    const [negativePrompt, setNegativePrompt] = useState(DEFAULT_NEGATIVE_PROMPT);

    // Settings state
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [width, setWidth] = useState(1024);
    const [height, setHeight] = useState(1024);
    const [steps, setSteps] = useState(25);
    const [cfgScale, setCfgScale] = useState(7);
    const [seed, setSeed] = useState(-1);
    const [lastUsedSeed, setLastUsedSeed] = useState<number | null>(null);

    // Generation state
    const [isGenerating, setIsGenerating] = useState(false);
    const [generatedImage, setGeneratedImage] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    // Quota state
    const [quota, setQuota] = useState<{ used: number; limit: number; remaining: number } | null>(null);

    // LoRA Modal state
    const [loraModalOpen, setLoraModalOpen] = useState(false);
    const [selectedLoraForModal, setSelectedLoraForModal] = useState<CivitaiModel | null>(null);
    const [modalLoraWeight, setModalLoraWeight] = useState(0.7);

    // Fetch checkpoints on mount
    useEffect(() => {
        fetchCheckpoints();
        fetchQuota();
    }, []);

    // Fetch LoRAs when search or page changes
    useEffect(() => {
        const debounce = setTimeout(() => {
            fetchLoras();
        }, 300);
        return () => clearTimeout(debounce);
    }, [loraSearch, loraPage]);

    const fetchCheckpoints = async () => {
        try {
            const res = await fetch('/api/image/civitai/checkpoints');
            const data = await res.json();
            setCheckpoints(data.models || []);
        } catch (err) {
            console.error('Failed to fetch checkpoints:', err);
        }
    };

    const fetchLoras = async () => {
        setLoadingLoras(true);
        try {
            const params = new URLSearchParams({
                type: 'LORA',
                query: loraSearch,
                page: loraPage.toString(),
                nsfw: 'true'
            });
            // Note: Not filtering by baseModel to get all LoRAs
            const res = await fetch(`/api/image/civitai/models?${params}`);
            const data = await res.json();
            setLoras(data.models || []);
            setLoraPagination(data.pagination);
        } catch (err) {
            console.error('Failed to fetch LoRAs:', err);
        } finally {
            setLoadingLoras(false);
        }
    };

    const fetchQuota = async () => {
        try {
            const res = await fetch('/api/image/quota');
            const data = await res.json();
            if (data.success) {
                setQuota(data.quota);
            }
        } catch (err) {
            console.error('Failed to fetch quota:', err);
        }
    };

    const addLora = (model: CivitaiModel) => {
        if (selectedLoras.length >= 5) {
            setError('Maximum 5 LoRAs allowed');
            return;
        }
        if (selectedLoras.find(l => l.id === model.id)) {
            return; // Already added
        }
        setSelectedLoras([...selectedLoras, {
            id: model.id,
            versionId: model.versionId,
            name: model.name,
            previewImage: model.previewImage,
            triggerWords: model.triggerWords,
            weight: 0.75
        }]);
    };

    const removeLora = (id: number) => {
        setSelectedLoras(selectedLoras.filter(l => l.id !== id));
    };

    const updateLoraWeight = (id: number, weight: number) => {
        setSelectedLoras(selectedLoras.map(l =>
            l.id === id ? { ...l, weight } : l
        ));
    };

    const setResolutionPreset = (preset: typeof RESOLUTION_PRESETS[0]) => {
        setWidth(preset.width);
        setHeight(preset.height);
    };

    // Open LoRA detail modal
    const openLoraModal = (lora: CivitaiModel) => {
        setSelectedLoraForModal(lora);
        setModalLoraWeight(0.7);
        setLoraModalOpen(true);
    };

    // Confirm LoRA selection from modal
    const confirmLoraSelection = () => {
        if (!selectedLoraForModal) return;

        if (selectedLoras.length >= 5) {
            setError('Maximum 5 LoRAs allowed');
            setLoraModalOpen(false);
            return;
        }
        if (selectedLoras.find(l => l.id === selectedLoraForModal.id)) {
            setLoraModalOpen(false);
            return; // Already added
        }

        setSelectedLoras([...selectedLoras, {
            id: selectedLoraForModal.id,
            versionId: selectedLoraForModal.versionId,
            name: selectedLoraForModal.name,
            previewImage: selectedLoraForModal.previewImage,
            triggerWords: selectedLoraForModal.triggerWords,
            weight: modalLoraWeight
        }]);

        setLoraModalOpen(false);
        setSelectedLoraForModal(null);
    };

    // Add trigger word to prompt
    const addTriggerToPrompt = (trigger: string) => {
        if (!prompt.includes(trigger)) {
            setPrompt(prev => prev ? `${prev}, ${trigger}` : trigger);
        }
    };

    // Add all triggers to prompt
    const addAllTriggersToPrompt = (triggers: string[]) => {
        const newTriggers = triggers.filter(t => !prompt.includes(t));
        if (newTriggers.length > 0) {
            setPrompt(prev => prev ? `${prev}, ${newTriggers.join(', ')}` : newTriggers.join(', '));
        }
    };

    const handleGenerate = async () => {
        if (!prompt.trim()) {
            setError('Please enter a prompt');
            return;
        }

        setIsGenerating(true);
        setError(null);

        try {
            const res = await fetch('/api/image/generate', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    prompt: prompt.trim(),
                    negativePrompt,
                    checkpointId: selectedCheckpoint?.id || null, // Added
                    checkpointVersionId: selectedCheckpoint?.versionId || null,
                    loraIds: selectedLoras.map(l => l.id),       // Added
                    loraVersionIds: selectedLoras.map(l => l.versionId),
                    loraScales: selectedLoras.map(l => l.weight),
                    width,
                    height,
                    steps,
                    cfgScale,
                    seed,
                })
            });

            const data = await res.json();

            if (!res.ok) {
                throw new Error(data.error || 'Generation failed');
            }

            setGeneratedImage(data.image);
            setLastUsedSeed(data.seed);
            if (data.quota) {
                setQuota(data.quota);
            }
        } catch (err: any) {
            setError(err.message || 'Generation failed');
        } finally {
            setIsGenerating(false);
        }
    };

    const handleDownload = async () => {
        if (!generatedImage) return;

        try {
            const res = await fetch(generatedImage);
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `ecstasyai-${Date.now()}.png`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Download failed:', err);
        }
    };

    const handleRegenerate = () => {
        setSeed(-1);
        handleGenerate();
    };

    return (
        <div className={`flex h-screen ${isDarkMode ? 'bg-[#0a0a14] text-gray-100' : 'bg-gray-50 text-gray-900'}`}>
            {/* Left Panel - Controls */}
            <div className={`w-[420px] min-w-[380px] flex flex-col border-r overflow-hidden ${
                isDarkMode ? 'bg-[#12121f] border-white/10' : 'bg-white border-gray-200'
            }`}>
                {/* Header */}
                <div className={`flex items-center gap-3 p-5 border-b ${isDarkMode ? 'border-white/10' : 'border-gray-100'}`}>
                    <div className="p-2 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500">
                        <Wand2 size={22} className="text-white" />
                    </div>
                    <div className="flex-1">
                        <h1 className="text-lg font-bold">Image Studio</h1>
                        <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                            Powered by Civitai
                        </p>
                    </div>
                    {quota && (
                        <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${
                            isDarkMode ? 'bg-violet-500/20 text-violet-300' : 'bg-violet-100 text-violet-600'
                        }`}>
                            <ImageIcon size={12} />
                            <span>{quota.remaining}/{quota.limit}</span>
                        </div>
                    )}
                </div>

                {/* Scrollable Content */}
                <div className="flex-1 overflow-y-auto p-5 space-y-5">
                    {/* Checkpoint Selector */}
                    <div className="space-y-2">
                        <label className={`flex items-center gap-2 text-sm font-semibold ${isDarkMode ? 'text-violet-400' : 'text-violet-600'}`}>
                            <Layers size={14} />
                            Model
                        </label>
                        <div className="relative">
                            <button
                                onClick={() => setCheckpointDropdownOpen(!checkpointDropdownOpen)}
                                className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border transition-all ${
                                    isDarkMode 
                                        ? 'bg-white/5 border-white/10 hover:border-violet-500/50' 
                                        : 'bg-gray-50 border-gray-200 hover:border-violet-400'
                                }`}
                            >
                                <div className="flex items-center gap-3">
                                    {selectedCheckpoint?.previewImage && (
                                        <img src={selectedCheckpoint.previewImage} alt="" className="w-8 h-8 rounded-lg object-cover" />
                                    )}
                                    <span className="text-sm font-medium truncate">
                                        {selectedCheckpoint?.name || 'Illustrious XL (Default)'}
                                    </span>
                                </div>
                                <ChevronDown size={16} className={`transition-transform ${checkpointDropdownOpen ? 'rotate-180' : ''}`} />
                            </button>
                            {checkpointDropdownOpen && (
                                <div className={`absolute z-50 w-full mt-2 rounded-xl border shadow-2xl max-h-72 overflow-y-auto ${
                                    isDarkMode ? 'bg-[#1a1a2e] border-white/10' : 'bg-white border-gray-200'
                                }`}>
                                    <div
                                        onClick={() => { setSelectedCheckpoint(null); setCheckpointDropdownOpen(false); }}
                                        className={`px-4 py-3 cursor-pointer transition-colors ${
                                            isDarkMode ? 'hover:bg-white/5' : 'hover:bg-gray-50'
                                        }`}
                                    >
                                        <span className="text-sm font-medium">Illustrious XL (Default)</span>
                                    </div>
                                    {checkpoints.map(cp => (
                                        <div
                                            key={cp.id}
                                            onClick={() => {
                                                setSelectedCheckpoint({ id: cp.id, versionId: cp.versionId, name: cp.name, previewImage: cp.previewImage });
                                                setCheckpointDropdownOpen(false);
                                            }}
                                            className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors ${
                                                isDarkMode ? 'hover:bg-white/5' : 'hover:bg-gray-50'
                                            }`}
                                        >
                                            {cp.previewImage && (
                                                <img src={cp.previewImage} alt="" className="w-10 h-10 rounded-lg object-cover" />
                                            )}
                                            <span className="text-sm truncate">{cp.name}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* LoRA Section */}
                    <div className="space-y-3">
                        <div className="flex items-center justify-between">
                            <label className={`flex items-center gap-2 text-sm font-semibold ${isDarkMode ? 'text-violet-400' : 'text-violet-600'}`}>
                                <SlidersHorizontal size={14} />
                                LoRAs
                            </label>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                                isDarkMode ? 'bg-white/10 text-gray-400' : 'bg-gray-100 text-gray-500'
                            }`}>
                                {selectedLoras.length}/5
                            </span>
                        </div>

                        {/* Search */}
                        <div className="relative">
                            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                            <input
                                type="text"
                                placeholder="Search characters, styles..."
                                value={loraSearch}
                                onChange={(e) => { setLoraSearch(e.target.value); setLoraPage(1); }}
                                className={`w-full pl-10 pr-4 py-2.5 rounded-xl border text-sm transition-all outline-none ${
                                    isDarkMode 
                                        ? 'bg-white/5 border-white/10 placeholder-gray-500 focus:border-violet-500/50' 
                                        : 'bg-gray-50 border-gray-200 placeholder-gray-400 focus:border-violet-400'
                                }`}
                            />
                        </div>

                        {/* Selected LoRAs */}
                        {selectedLoras.length > 0 && (
                            <div className="space-y-2">
                                {selectedLoras.map(lora => (
                                    <div key={lora.id} className={`flex items-center gap-3 p-3 rounded-xl ${
                                        isDarkMode ? 'bg-violet-500/10 border border-violet-500/20' : 'bg-violet-50 border border-violet-100'
                                    }`}>
                                        {lora.previewImage && (
                                            <img src={lora.previewImage} alt="" className="w-10 h-10 rounded-lg object-cover" />
                                        )}
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium truncate">{lora.name}</p>
                                            <div className="flex items-center gap-2 mt-1">
                                                <input
                                                    type="range"
                                                    min="0"
                                                    max="1"
                                                    step="0.05"
                                                    value={lora.weight}
                                                    onChange={(e) => updateLoraWeight(lora.id, parseFloat(e.target.value))}
                                                    className="flex-1 h-1 accent-violet-500"
                                                />
                                                <span className="text-xs text-gray-500 w-8">{lora.weight.toFixed(2)}</span>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => removeLora(lora.id)}
                                            className={`p-1.5 rounded-lg transition-colors ${
                                                isDarkMode ? 'hover:bg-white/10' : 'hover:bg-violet-100'
                                            }`}
                                        >
                                            <X size={14} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* LoRA Grid */}
                        <div className="grid grid-cols-4 gap-2 max-h-48 overflow-y-auto">
                            {loadingLoras ? (
                                <div className="col-span-4 flex justify-center py-8">
                                    <Loader2 size={24} className="animate-spin text-violet-500" />
                                </div>
                            ) : (
                                loras.map(lora => (
                                    <div
                                        key={lora.id}
                                        onClick={() => openLoraModal(lora)}
                                        className={`group relative rounded-xl overflow-hidden cursor-pointer border-2 transition-all ${
                                            selectedLoras.find(l => l.id === lora.id)
                                                ? 'border-violet-500 ring-2 ring-violet-500/30'
                                                : isDarkMode ? 'border-transparent hover:border-white/20' : 'border-transparent hover:border-gray-300'
                                        }`}
                                    >
                                        {lora.previewImage ? (
                                            <img src={lora.previewImage} alt={lora.name} className="w-full aspect-square object-cover" />
                                        ) : (
                                            <div className={`w-full aspect-square flex items-center justify-center ${
                                                isDarkMode ? 'bg-white/5' : 'bg-gray-100'
                                            }`}>
                                                <ImageIcon size={20} className="text-gray-400" />
                                            </div>
                                        )}
                                        <div className={`absolute inset-x-0 bottom-0 p-1.5 text-[10px] truncate ${
                                            isDarkMode ? 'bg-black/60' : 'bg-white/80'
                                        }`}>
                                            {lora.name}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>

                        {/* Pagination */}
                        {loraPagination && loraPagination.totalPages > 1 && (
                            <div className="flex items-center justify-center gap-3">
                                <button
                                    onClick={() => setLoraPage(Math.max(1, loraPage - 1))}
                                    disabled={loraPage === 1}
                                    className={`p-2 rounded-lg transition-colors disabled:opacity-40 ${
                                        isDarkMode ? 'hover:bg-white/10' : 'hover:bg-gray-100'
                                    }`}
                                >
                                    <ChevronDown size={16} className="rotate-90" />
                                </button>
                                <span className="text-xs text-gray-500">{loraPage} / {loraPagination.totalPages}</span>
                                <button
                                    onClick={() => setLoraPage(Math.min(loraPagination.totalPages, loraPage + 1))}
                                    disabled={loraPage === loraPagination.totalPages}
                                    className={`p-2 rounded-lg transition-colors disabled:opacity-40 ${
                                        isDarkMode ? 'hover:bg-white/10' : 'hover:bg-gray-100'
                                    }`}
                                >
                                    <ChevronDown size={16} className="-rotate-90" />
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Prompt */}
                    <div className="space-y-2">
                        <label className={`flex items-center gap-2 text-sm font-semibold ${isDarkMode ? 'text-violet-400' : 'text-violet-600'}`}>
                            <Sparkles size={14} />
                            Prompt
                        </label>
                        <textarea
                            placeholder="Describe your image... masterpiece, best quality, 1girl, anime style"
                            value={prompt}
                            onChange={(e) => setPrompt(e.target.value)}
                            rows={4}
                            className={`w-full px-4 py-3 rounded-xl border text-sm resize-none transition-all outline-none ${
                                isDarkMode 
                                    ? 'bg-white/5 border-white/10 placeholder-gray-500 focus:border-violet-500/50' 
                                    : 'bg-gray-50 border-gray-200 placeholder-gray-400 focus:border-violet-400'
                            }`}
                        />
                    </div>

                    {/* Advanced Settings Toggle */}
                    <button
                        onClick={() => setSettingsOpen(!settingsOpen)}
                        className={`w-full flex items-center justify-between px-4 py-3 rounded-xl transition-colors ${
                            isDarkMode ? 'bg-white/5 hover:bg-white/10' : 'bg-gray-50 hover:bg-gray-100'
                        }`}
                    >
                        <div className="flex items-center gap-2">
                            <Settings2 size={16} className={isDarkMode ? 'text-violet-400' : 'text-violet-600'} />
                            <span className="text-sm font-medium">Advanced Settings</span>
                        </div>
                        <ChevronDown size={16} className={`transition-transform ${settingsOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {/* Settings Panel */}
                    {settingsOpen && (
                        <div className={`space-y-4 p-4 rounded-xl ${isDarkMode ? 'bg-white/5' : 'bg-gray-50'}`}>
                            {/* Negative Prompt */}
                            <div className="space-y-2">
                                <label className="text-xs font-medium text-gray-500">Negative Prompt</label>
                                <textarea
                                    value={negativePrompt}
                                    onChange={(e) => setNegativePrompt(e.target.value)}
                                    rows={2}
                                    className={`w-full px-3 py-2 rounded-lg border text-xs transition-all outline-none ${
                                        isDarkMode 
                                            ? 'bg-black/30 border-white/10 focus:border-violet-500/50' 
                                            : 'bg-white border-gray-200 focus:border-violet-400'
                                    }`}
                                />
                            </div>

                            {/* Resolution */}
                            <div className="space-y-2">
                                <label className="text-xs font-medium text-gray-500">Resolution</label>
                                <div className="grid grid-cols-3 gap-2">
                                    {RESOLUTION_PRESETS.map(preset => (
                                        <button
                                            key={preset.label}
                                            onClick={() => setResolutionPreset(preset)}
                                            className={`py-2 px-3 rounded-lg text-xs font-medium transition-all ${
                                                width === preset.width && height === preset.height
                                                    ? 'bg-violet-500 text-white'
                                                    : isDarkMode ? 'bg-white/10 hover:bg-white/20' : 'bg-gray-100 hover:bg-gray-200'
                                            }`}
                                        >
                                            <div>{preset.label}</div>
                                            <div className="text-[10px] opacity-70">{preset.ratio}</div>
                                        </button>
                                    ))}
                                </div>
                                <div className="flex items-center gap-2 mt-2">
                                    <input
                                        type="number"
                                        value={width}
                                        onChange={(e) => setWidth(parseInt(e.target.value) || 512)}
                                        step={64}
                                        className={`w-20 px-2 py-1.5 rounded-lg border text-xs text-center outline-none ${
                                            isDarkMode ? 'bg-black/30 border-white/10' : 'bg-white border-gray-200'
                                        }`}
                                    />
                                    <X size={12} className="text-gray-500" />
                                    <input
                                        type="number"
                                        value={height}
                                        onChange={(e) => setHeight(parseInt(e.target.value) || 512)}
                                        step={64}
                                        className={`w-20 px-2 py-1.5 rounded-lg border text-xs text-center outline-none ${
                                            isDarkMode ? 'bg-black/30 border-white/10' : 'bg-white border-gray-200'
                                        }`}
                                    />
                                </div>
                            </div>

                            {/* Steps & CFG */}
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <div className="flex justify-between">
                                        <label className="text-xs font-medium text-gray-500">Steps</label>
                                        <span className="text-xs text-violet-500 font-medium">{steps}</span>
                                    </div>
                                    <input
                                        type="range"
                                        min="10"
                                        max="50"
                                        value={steps}
                                        onChange={(e) => setSteps(parseInt(e.target.value))}
                                        className="w-full h-1 accent-violet-500"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <div className="flex justify-between">
                                        <label className="text-xs font-medium text-gray-500">CFG Scale</label>
                                        <span className="text-xs text-violet-500 font-medium">{cfgScale}</span>
                                    </div>
                                    <input
                                        type="range"
                                        min="1"
                                        max="15"
                                        step="0.5"
                                        value={cfgScale}
                                        onChange={(e) => setCfgScale(parseFloat(e.target.value))}
                                        className="w-full h-1 accent-violet-500"
                                    />
                                </div>
                            </div>

                            {/* Seed */}
                            <div className="space-y-2">
                                <label className="text-xs font-medium text-gray-500">Seed</label>
                                <div className="flex gap-2">
                                    <input
                                        type="number"
                                        value={seed}
                                        onChange={(e) => setSeed(parseInt(e.target.value) || -1)}
                                        placeholder="-1 = random"
                                        className={`flex-1 px-3 py-2 rounded-lg border text-xs outline-none ${
                                            isDarkMode ? 'bg-black/30 border-white/10' : 'bg-white border-gray-200'
                                        }`}
                                    />
                                    <button
                                        onClick={() => setSeed(-1)}
                                        title="Random"
                                        className={`p-2 rounded-lg transition-colors ${
                                            isDarkMode ? 'bg-white/10 hover:bg-white/20' : 'bg-gray-100 hover:bg-gray-200'
                                        }`}
                                    >
                                        <Dice5 size={16} />
                                    </button>
                                    <button
                                        onClick={() => lastUsedSeed && setSeed(lastUsedSeed)}
                                        title="Reuse last"
                                        disabled={!lastUsedSeed}
                                        className={`p-2 rounded-lg transition-colors disabled:opacity-40 ${
                                            isDarkMode ? 'bg-white/10 hover:bg-white/20' : 'bg-gray-100 hover:bg-gray-200'
                                        }`}
                                    >
                                        <RefreshCw size={16} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer - Generate Button */}
                <div className={`p-5 border-t ${isDarkMode ? 'border-white/10' : 'border-gray-100'}`}>
                    {error && (
                        <div className="flex items-center gap-2 p-3 mb-3 rounded-xl bg-red-500/10 text-red-500 text-sm">
                            <AlertCircle size={16} />
                            <span>{error}</span>
                        </div>
                    )}
                    <button
                        onClick={handleGenerate}
                        disabled={isGenerating || !prompt.trim()}
                        className={`w-full flex items-center justify-center gap-2 py-4 rounded-xl font-semibold text-white transition-all ${
                            isGenerating || !prompt.trim()
                                ? 'bg-violet-500/50 cursor-not-allowed'
                                : 'bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:from-violet-600 hover:to-fuchsia-600 shadow-lg shadow-violet-500/25'
                        }`}
                    >
                        {isGenerating ? (
                            <>
                                <Loader2 size={18} className="animate-spin" />
                                Generating...
                            </>
                        ) : (
                            <>
                                <Wand2 size={18} />
                                Generate Image
                            </>
                        )}
                    </button>
                </div>
            </div>

            {/* Right Panel - Preview */}
            <div className={`flex-1 flex flex-col items-center justify-center p-8 ${
                isDarkMode ? 'bg-[#0a0a14]' : 'bg-gray-100'
            }`}>
                {isGenerating ? (
                    <div className="text-center">
                        <div className="relative">
                            <div className="w-20 h-20 rounded-full border-4 border-violet-500/30 border-t-violet-500 animate-spin" />
                            <Wand2 size={24} className="absolute inset-0 m-auto text-violet-500" />
                        </div>
                        <p className={`mt-6 font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                            Creating your masterpiece...
                        </p>
                        <p className={`text-sm mt-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                            This may take 15-60 seconds
                        </p>
                    </div>
                ) : generatedImage ? (
                    <div className="text-center max-w-full">
                        <img
                            src={generatedImage}
                            alt="Generated"
                            className="max-w-full max-h-[calc(100vh-200px)] rounded-2xl shadow-2xl"
                        />
                        {lastUsedSeed && (
                            <div className={`flex items-center justify-center gap-2 mt-4 text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                <span>Seed: {lastUsedSeed}</span>
                                <button
                                    onClick={() => navigator.clipboard.writeText(String(lastUsedSeed))}
                                    className="p-1 rounded hover:bg-white/10 transition-colors"
                                    title="Copy seed"
                                >
                                    <Copy size={14} />
                                </button>
                            </div>
                        )}
                        <div className="flex gap-3 justify-center mt-4">
                            <button
                                onClick={handleDownload}
                                className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium transition-all ${
                                    isDarkMode 
                                        ? 'bg-white/10 hover:bg-white/20' 
                                        : 'bg-gray-200 hover:bg-gray-300'
                                }`}
                            >
                                <Download size={16} />
                                Download
                            </button>
                            <button
                                onClick={handleRegenerate}
                                className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium transition-all ${
                                    isDarkMode 
                                        ? 'bg-white/10 hover:bg-white/20' 
                                        : 'bg-gray-200 hover:bg-gray-300'
                                }`}
                            >
                                <RefreshCw size={16} />
                                Regenerate
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className={`text-center ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}>
                        <div className={`w-24 h-24 mx-auto mb-6 rounded-2xl flex items-center justify-center ${
                            isDarkMode ? 'bg-white/5' : 'bg-gray-200'
                        }`}>
                            <ImageIcon size={40} className="opacity-40" />
                        </div>
                        <p className="font-medium">Your image will appear here</p>
                        <p className="text-sm mt-2">Enter a prompt and click Generate</p>
                    </div>
                )}
            </div>

            {/* Spinner CSS */}
            <style>{`
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
            `}</style>

            {/* LoRA Detail Modal */}
            {loraModalOpen && selectedLoraForModal && (
                <div 
                    className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-5"
                    onClick={() => setLoraModalOpen(false)}
                >
                    <div
                        className={`w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl ${
                            isDarkMode ? 'bg-[#1a1a2e]' : 'bg-white'
                        }`}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Modal Header */}
                        <div className={`flex items-center justify-between p-5 border-b ${
                            isDarkMode ? 'border-white/10' : 'border-gray-100'
                        }`}>
                            <h3 className="text-lg font-bold truncate pr-4">{selectedLoraForModal.name}</h3>
                            <button
                                onClick={() => setLoraModalOpen(false)}
                                className={`p-2 rounded-lg transition-colors ${
                                    isDarkMode ? 'hover:bg-white/10' : 'hover:bg-gray-100'
                                }`}
                            >
                                <X size={20} />
                            </button>
                        </div>

                        {/* Modal Content */}
                        <div className="p-5 space-y-5">
                            {/* Preview and Info */}
                            <div className="flex gap-4">
                                {selectedLoraForModal.previewImage && (
                                    <img
                                        src={selectedLoraForModal.previewImage}
                                        alt={selectedLoraForModal.name}
                                        className="w-28 h-28 rounded-xl object-cover flex-shrink-0"
                                    />
                                )}
                                <div className="flex-1 min-w-0">
                                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                                        isDarkMode ? 'bg-violet-500/20 text-violet-300' : 'bg-violet-100 text-violet-600'
                                    }`}>
                                        LoRA
                                    </span>
                                    {selectedLoraForModal.tags && selectedLoraForModal.tags.length > 0 && (
                                        <p className="text-xs text-gray-500 mt-3 line-clamp-3">
                                            {selectedLoraForModal.tags.slice(0, 5).join(', ')}
                                        </p>
                                    )}
                                </div>
                            </div>

                            {/* Trigger Words */}
                            {selectedLoraForModal.triggerWords && selectedLoraForModal.triggerWords.length > 0 && (
                                <div className="space-y-3">
                                    <div className="flex items-center justify-between">
                                        <span className={`text-sm font-semibold ${isDarkMode ? 'text-violet-400' : 'text-violet-600'}`}>
                                            Trigger Words
                                        </span>
                                        <button
                                            onClick={() => addAllTriggersToPrompt(selectedLoraForModal.triggerWords)}
                                            className="text-xs font-semibold text-violet-500 hover:text-violet-400 transition-colors"
                                        >
                                            Add All
                                        </button>
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        {selectedLoraForModal.triggerWords.map((trigger, idx) => (
                                            <button
                                                key={idx}
                                                onClick={() => addTriggerToPrompt(trigger)}
                                                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all flex items-center gap-1 ${
                                                    prompt.includes(trigger)
                                                        ? 'bg-violet-500 text-white'
                                                        : isDarkMode 
                                                            ? 'bg-white/10 hover:bg-white/20' 
                                                            : 'bg-gray-100 hover:bg-gray-200'
                                                }`}
                                            >
                                                {trigger}
                                                {!prompt.includes(trigger) && <Plus size={12} className="opacity-50" />}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Weight Slider */}
                            <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                    <span className={`text-sm font-semibold ${isDarkMode ? 'text-violet-400' : 'text-violet-600'}`}>
                                        Weight
                                    </span>
                                    <input
                                        type="number"
                                        value={modalLoraWeight}
                                        onChange={(e) => setModalLoraWeight(Math.min(1, Math.max(0, parseFloat(e.target.value) || 0)))}
                                        step={0.05}
                                        min={0}
                                        max={1}
                                        className={`w-16 px-2 py-1 rounded-lg border text-center text-sm outline-none ${
                                            isDarkMode ? 'bg-white/5 border-white/10' : 'bg-gray-50 border-gray-200'
                                        }`}
                                    />
                                </div>
                                <input
                                    type="range"
                                    min="0"
                                    max="1"
                                    step="0.05"
                                    value={modalLoraWeight}
                                    onChange={(e) => setModalLoraWeight(parseFloat(e.target.value))}
                                    className="w-full h-2 accent-violet-500"
                                />
                            </div>

                            {/* Action Buttons */}
                            <div className="flex gap-3 pt-2">
                                <a
                                    href={`https://civitai.com/models/${selectedLoraForModal.id}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-semibold text-sm border transition-all ${
                                        isDarkMode 
                                            ? 'border-white/20 hover:bg-white/5' 
                                            : 'border-gray-200 hover:bg-gray-50'
                                    }`}
                                >
                                    <ExternalLink size={16} />
                                    View on Civitai
                                </a>
                                <button
                                    onClick={confirmLoraSelection}
                                    className="flex-1 py-3 rounded-xl font-semibold text-sm text-white bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:from-violet-600 hover:to-fuchsia-600 transition-all shadow-lg shadow-violet-500/25"
                                >
                                    Use this LoRA
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ImageGenerate;
